import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  EventType,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type PublicClientApplication,
} from '@azure/msal-browser';
import { useConfig } from '@/config/useConfig';
import { APP_BASENAME } from '@/routing/basename';
import { resolveAuthMode } from './authMode';

export interface AuthUser {
  name: string;
  email: string;
  oid: string;
}

export interface TokenResult {
  accessToken: string;
  /**
   * OIDC ID token (aud = client id). During the ID-token auth migration (WI 776915) this is the
   * bearer the AITA backend expects; callers send `idToken ?? accessToken` so legacy access-token
   * stubs/paths still work. Populated from MSAL's AuthenticationResult.idToken.
   */
  idToken?: string;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
  /**
   * Error message from a failed login-redirect return (e.g. AADSTS500011 for a
   * tenant without the app's service principal), or null. Landing surfaces it.
   */
  signInError: string | null;
  acquireTokenSilent(request?: { scopes?: string[] }): Promise<TokenResult>;
  /**
   * Starts sign-in. `intendedPath` is the router path to land on after auth completes.
   * In MSAL mode this triggers a full-page redirect — the returned promise never
   * resolves; the path is restored post-redirect via the OAuth `state` parameter.
   * The dev stub ignores the argument (it is already authenticated).
   */
  login(intendedPath?: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Dev stub user — used when the resolved auth mode is 'stub'
const DEV_USER: AuthUser = {
  name: 'Dev User',
  email: 'dev@microsoft.com',
  oid: '00000000-0000-0000-0000-000000000000',
};

function createDevStubContext(): AuthContextValue {
  return {
    isAuthenticated: true,
    user: DEV_USER,
    isLoading: false,
    signInError: null,
    acquireTokenSilent: async () => ({ accessToken: 'dev-token-stub' }),
    login: async () => {},
    logout: async () => {
      window.location.href = '/';
    },
  };
}

/**
 * Router path for the current page, used as MSAL `state` so a token-redirect
 * round-trip restores the user's position. Strips the shared APP_BASENAME
 * (the same constant BrowserRouter mounts with).
 */
function currentRouterPath(): string {
  let pathname = window.location.pathname;
  if (pathname.toLowerCase().startsWith(APP_BASENAME.toLowerCase())) {
    pathname = pathname.slice(APP_BASENAME.length);
  }
  const path = pathname + window.location.search;
  return path === '' || path === '/' ? '/app' : path;
}

interface MsalAuthProviderProps {
  children: ReactNode;
  pca: PublicClientApplication;
  /** Error from the bootstrap redirect return, surfaced via context */
  signInError?: string | null;
}

function MsalAuthProvider({ children, pca, signInError = null }: MsalAuthProviderProps): React.ReactElement {
  const config = useConfig();
  const [account, setAccount] = useState<AccountInfo | null>(() => pca.getActiveAccount());

  useEffect(() => {
    const callbackId = pca.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const payload = event.payload as AuthenticationResult | null;
        if (payload?.account) {
          pca.setActiveAccount(payload.account);
          setAccount(payload.account);
        }
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setAccount(null);
      }
    });
    return () => {
      if (callbackId) {
        pca.removeEventCallback(callbackId);
      }
    };
  }, [pca]);

  const authValue = useMemo<AuthContextValue>(() => {
    return {
      isAuthenticated: account !== null,
      // AccountInfo.localAccountId carries the oid claim and is non-optional.
      user: account
        ? {
            name: account.name ?? account.username,
            email: account.username,
            oid: account.localAccountId,
          }
        : null,
      isLoading: false,
      signInError,
      acquireTokenSilent: async (request?: { scopes?: string[] }) => {
        const active = pca.getActiveAccount();
        if (!active) {
          throw new Error('No active account — sign in before requesting tokens.');
        }
        const scopes = request?.scopes ?? config.msalScopes;
        try {
          const result = await pca.acquireTokenSilent({ scopes, account: active });
          return { accessToken: result.accessToken, idToken: result.idToken };
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            // Full-page redirect imminent — give in-flight work a chance to
            // stash state (AssessmentProvider persists pending responses).
            window.dispatchEvent(new CustomEvent('aita:before-auth-redirect'));
            await pca.acquireTokenRedirect({ scopes, state: currentRouterPath() });
            // acquireTokenRedirect navigates away — never resolve.
            return new Promise<TokenResult>(() => {});
          }
          throw error;
        }
      },
      login: async (intendedPath?: string) => {
        // Full-page redirect: this promise never resolves. Path restore happens
        // post-redirect via createMsal()'s redirectPath (OAuth state).
        // prompt:'select_account' forces the account picker — without it AAD
        // silently SSOs the browser's existing session, which on a corp-joined
        // machine is the corp account and fails with AADSTS500011 (no service
        // principal for this app in that tenant).
        await pca.loginRedirect({
          scopes: config.msalScopes,
          state: intendedPath,
          prompt: 'select_account',
        });
      },
      logout: async () => {
        await pca.logoutRedirect();
      },
    };
  }, [account, pca, config.msalScopes, signInError]);

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
}

function StaticAuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const config = useConfig();

  const authValue = useMemo<AuthContextValue>(() => {
    // Dev stub when the resolved mode is 'stub' (explicit, or defaulted for local)
    if (resolveAuthMode(config) === 'stub') {
      return createDevStubContext();
    }

    // authMode resolved to 'msal' but no PublicClientApplication was provided —
    // fail closed (bootstrap in main.tsx is responsible for passing `pca`).
    const notConfiguredError = new Error(
      `Authentication is not configured for environment "${config.environment}".`,
    );

    return {
      isAuthenticated: false,
      user: null,
      isLoading: false,
      signInError: null,
      acquireTokenSilent: async () => {
        throw notConfiguredError;
      },
      login: async () => {
        throw notConfiguredError;
      },
      logout: async () => {},
    };
  }, [config]);

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
}

interface AuthProviderProps {
  children: ReactNode;
  /** Override for testing — inject a mock auth context */
  value?: AuthContextValue;
  /** Initialized MSAL instance (from createMsal) — presence selects the MSAL branch */
  pca?: PublicClientApplication;
  /** Error from the bootstrap login-redirect return, if any */
  signInError?: string | null;
}

export function AuthProvider({ children, value, pca, signInError }: AuthProviderProps): React.ReactElement {
  // Branch precedence: test injection → MSAL → stub/fail-closed.
  if (value) {
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
  }
  if (pca) {
    return (
      <MsalAuthProvider pca={pca} signInError={signInError}>
        {children}
      </MsalAuthProvider>
    );
  }
  return <StaticAuthProvider>{children}</StaticAuthProvider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
